import React from "react";
import styles from "./InboundOutboundVoiceAI.module.css";
import {
    PhoneIncoming,
    PhoneOutgoing,
    Check,
    GitBranch,
    MessageSquare,
    Clock,
    RotateCcw,
    Zap,
    FileText
} from "lucide-react";

const inboundFeatures = [
    "24/7 customer support hotline",
    "IVR replacement with natural AI",
    "Intelligent call routing",
    "FAQ and troubleshooting",
    "Order status and tracking",
    "Appointment confirmations",
];

const outboundFeatures = [
    "Sales and lead qualification",
    "Payment and EMI reminders",
    "Collections and recovery",
    "Appointment scheduling",
    "Survey and feedback calls",
    "Promotional campaigns",
];

const workflows = [
    {
        icon: <GitBranch />,
        title: "Conditional Routing",
        desc: "Branch conversations based on customer responses, payment status, or any custom logic.",
    },
    {
        icon: <MessageSquare />,
        title: "WhatsApp & SMS",
        desc: "Seamlessly switch between voice, WhatsApp, and SMS within the same workflow.",
    },
    {
        icon: <Clock />,
        title: "Scheduled Calling",
        desc: "Schedule calls for optimal times. Auto-retry failed attempts with smart backoff.",
    },
    {
        icon: <RotateCcw />,
        title: "Loop & Retry Logic",
        desc: "Build retry loops, follow-up sequences, and multi-attempt campaigns.",
    },
    {
        icon: <Zap />,
        title: "Live Function Calls",
        desc: "Trigger real-time API calls mid-conversation to fetch data or update systems.",
    },
    {
        icon: <FileText />,
        title: "Post-Call Extraction",
        desc: "Auto-extract summaries, sentiment scores, structured data, and action items.",
    },
];

const InboundOutboundVoiceAI = () => {
    return (
        <section className={styles.wrapper}>
            {/* Top Section */}
            <h2 className={styles.title}>Inbound and Outbound Voice AI</h2>
            <p className={styles.subtitle}>
                Handle both incoming calls and proactive outreach with a single platform.
            </p>

            <div className={styles.cardGrid}>
                {/* Inbound */}
                <div className={`${styles.card} ${styles.inbound}`}>
                    <div className={styles.cardHeader}>
                        <div className={`${styles.iconBox} ${styles.green}`}>
                            <PhoneIncoming />
                        </div>
                        <div>
                            <h3>Inbound Calls</h3>
                            <p>Customer-initiated conversations</p>
                        </div>
                    </div>

                    <ul>
                        {inboundFeatures.map((item, i) => (
                            <li key={i}>
                                <span className={styles.checkGreen}><Check /></span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Outbound */}
                <div className={`${styles.card} ${styles.outbound}`}>
                    <div className={styles.cardHeader}>
                        <div className={`${styles.iconBox} ${styles.blue}`}>
                            <PhoneOutgoing />
                        </div>
                        <div>
                            <h3>Outbound Calls</h3>
                            <p>Proactive customer outreach</p>
                        </div>
                    </div>

                    <ul>
                        {outboundFeatures.map((item, i) => (
                            <li key={i}>
                                <span className={styles.checkBlue}><Check /></span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Workflow Section */}
            <div className={styles.workflowSection}>
                <h2 className={styles.title}>Complex Workflows, Zero Code</h2>
                <p className={styles.subtitle}>
                    Build sophisticated voice automation with our visual workflow builder.
                </p>

                <div className={styles.workflowGrid}>
                    {workflows.map((item, i) => (
                        <div
                            key={i}
                            className={styles.workflowCard}
                            style={{ animationDelay: `${i * 0.08}s` }}
                        >
                            <div className={styles.workflowIcon}>{item.icon}</div>
                            <h4>{item.title}</h4>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default InboundOutboundVoiceAI;